/**
 * Which column a piece of blocked time belongs to — and what to do when the
 * answer is "none of them".
 *
 *   "Also trying to block time I think it blocked but can we see it on the
 *    calendar like I blocked out this Wednesday for me a meeting from 1-2 pm
 *    but it's not showing on the calendar"
 *
 * It had blocked. She blocked HERSELF, the grid was grouped by seat, and a
 * provider block carries no room, so the day grid quietly dropped it and the
 * hour read as free. A block with no column of its own is real blocked time
 * and has to draw somewhere, so it bands the whole day instead.
 */

export interface BlockLike {
  provider_id: string | null;
  room_id: string | null;
}

export type GroupBy = "provider" | "room";

export interface SplitBlocks<T> {
  /** Keyed by column id — the seats or providers the grid is drawing. */
  byColumn: Map<string, T[]>;
  /** Blocks belonging to no column on this grid: drawn across the day. */
  across: T[];
}

export function splitBlocks<T extends BlockLike>(
  blocks: readonly T[],
  columnIds: readonly (string | null)[],
  groupBy: GroupBy,
): SplitBlocks<T> {
  const known = new Set(columnIds.filter((id): id is string => !!id));
  const byColumn = new Map<string, T[]>();
  for (const id of known) byColumn.set(id, []);
  const across: T[] = [];
  for (const b of blocks) {
    const key = groupBy === "provider" ? b.provider_id : b.room_id;
    // A seat that's been retired off the board has no column either, and its
    // block used to vanish with it.
    if (key && known.has(key)) byColumn.get(key)!.push(b);
    else across.push(b);
  }
  return { byColumn, across };
}
